import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import useUnauthorizedHandler from "../snippet/useUnauthorizedHandler";
import { debounce } from "../snippet/Debouncing";
import { GetProviderStates } from "../../actions/Admin/reports/GetProviderStates";

function AllStatesMultiSelectDropdown({ title, updateFilter }) {
  const [state_ids, setState_ids] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]);
  const dropdownRef = useRef(null);
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const dispatch = useDispatch();

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const inputchangeSearch = useCallback(debounce(handleSearchChange), []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (item) => {
    const newEntry = parseInt(item.state_id, 10);
    const exists = state_ids.includes(newEntry);
    if (exists) {
      setState_ids(prevState => prevState.filter(entry => entry !== newEntry));
    } else {
      setState_ids(prevState => [...prevState, newEntry]);
    }
  };

  const clearSelection = () => {
    setState_ids([]);
  };

  useEffect(() => {
    updateFilter("states", state_ids);
  }, [state_ids]);

  const fetchStates = useCallback(async () => {
    try {
      const res = await dispatch(GetProviderStates());
      
      if (res?.success && Array.isArray(res?.data)) {
        setItems(
          res.data.map(item => ({
            state_name: item.state_name,
            state_id: item.state_id,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  const filteredItems = items.filter(item => 
    item.state_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative inline-block text-left w-40" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        type="button"
        className="flex items-center justify-between w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <span className="truncate">{title}</span>
        {state_ids?.length > 0 && (
          <span className="inline-flex ml-1 items-center justify-center w-6 h-6 text-sm font-semibold text-blue-700 bg-blue-100 rounded-full">
            {state_ids.length}
          </span>
        )}
        <svg
          className="w-5 h-5 ml-2 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10">
          <div className="flex items-center justify-between p-2 border-b border-gray-200">
            <span className="text-sm text-gray-500">
              {state_ids.length} selected
            </span>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline focus:outline-none"
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>

          <div className="p-2">
            <input
              type="text"
              placeholder="Search states"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              onChange={(e) => inputchangeSearch(e)}
            />
          </div>

          <ul className="max-h-48 overflow-y-auto">
            {filteredItems?.map((item) => (
              <li
                key={item.state_id}
                className="flex items-center px-3 py-2 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 mr-2 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={state_ids.includes(parseInt(item.state_id, 10))}
                  onChange={() => handleSelect(item)}
                />
                <span className="text-sm text-gray-700">
                  {item.state_name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default AllStatesMultiSelectDropdown;
